<script lang="ts" setup>
import type { Group, GroupMember } from '~/models/Group'
import { useI18n } from 'vue-i18n'
import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { faXmark } from '@fortawesome/duotone-regular-svg-icons'
import { AuToast } from '~/stores/toasts'
import useGroupsStore from '~/stores/groups'
import { useAuthStore } from '~/stores/auth'
import { UserRole } from '~/models/User/UserRole'
import { GroupRole, GroupSource, GroupType } from '~/models/Group'
import Button from '~/components/ui/Button.vue'
import AuSpinner from '~/components/ui/AuSpinner.vue'
import SearchMembers from '~/components/settings/SettingsGroups/SearchMembers.vue'

const props = defineProps<{
  group: Group
  groupMembers: GroupMember[]
}>()

const emit = defineEmits(['changeView', 'back', 'groupCreated'])

const { t } = useI18n()

let initialGroupMembers: GroupMember[] = []

const groupName = ref('')
const newGroup = ref(false)
const newGroupMembers = ref<Array<GroupMember>>([])

const { createGroup, sortMembers } = useGroupsStore()
const { isLoading } = storeToRefs(useGroupsStore())
const { user, userId, userRole } = storeToRefs(useAuthStore())

const isFeide = computed(() => props.group?.groupSource === GroupSource.Feide)

const readyToSave = computed(() => {
  if (!newGroup.value) {
    return props.group.name !== groupName.value
      || newGroupMembers.value !== initialGroupMembers
      || (props.group && !props.group.groupId)
  }
  return groupName.value && newGroupMembers.value.length > 0
})

const currentUserInMembers = computed(() => {
  return Boolean([...newGroupMembers.value, ...props.groupMembers]
    .find(m => m.userId === userId.value))
})

async function populateGroup() {
  const group = { ...props.group }
  group.name = groupName.value
  group.groupSource = group.groupSource || GroupSource.Local
  group.groupType = group.groupType || GroupType.Adhoc
  try {
    if (!currentUserInMembers.value && userId.value) {
      newGroupMembers.value = [...newGroupMembers.value, {
        userId: userId.value,
        firstName: user.value?.firstName || '',
        lastName: user.value?.lastName || '',
        fullName: user.value?.fullName || '',
        role: userRole.value,
        groupRole: GroupRole.Admin,
      }]
    }
    const [newGroup] = await createGroup(group, newGroupMembers.value)

    emit('groupCreated', newGroup)
    AuToast.success(t('groups.saveSuccess'))
  } catch (error) {
    AuToast.error(t('groups.saveError'))
    throw error
  } finally {
    emit('changeView')
  }
}

const newGroupMembersSorted = computed(() => {
  const teachers = newGroupMembers.value.filter(({ role }) => role === UserRole.Teacher).sort(sortMembers)
  const students = newGroupMembers.value.filter(({ role }) => role !== UserRole.Teacher).sort(sortMembers)
  return [teachers, students].flatMap(member => member)
})

const removeGroupMember = (userId: string) => {
  newGroupMembers.value = newGroupMembers.value.filter(member => member.userId !== userId)
}

onMounted(() => {
  if (!Object.keys(props.group).length) {
    newGroup.value = true
    return
  }
  groupName.value = props.group.name
  initialGroupMembers = props.groupMembers
  newGroupMembers.value = props.groupMembers
})
</script>

<template>
  <div class="animate-fade relative flex h-full flex-col gap-6 py-6">
    <label class="flex flex-col gap-1">
      <span
        class="font-medium"
        v-text="t('groups.groupName')"
      />
      <input
        v-model="groupName"
        v-focus
        :disabled="isFeide"
        :placeholder="t('groups.groupNamePlaceholder')"
        class="hover:not-disabled:outline-green-10 outline-green-5 text-green-5 hover:not-disabled:bg-green-5/10 focus-visible:bg-green-5/10 placeholder:text-green-10 rounded-xl bg-transparent p-3 outline -outline-offset-1 transition-all  focus-visible:ring"
        type="text"
      >
    </label>
    <div>
      <label class="flex w-full flex-col gap-1">
        <span v-text="t('groups.members')" />
        <SearchMembers
          v-if="!isFeide"
          v-model="newGroupMembers"
          shade="light"
        />
      </label>
      <ul
        v-if="newGroupMembersSorted.length"
        class="my-3"
      >
        <li
          v-for="member in newGroupMembersSorted"
          :key="`${props.group.groupId}-${member.userId}`"
          class="animate-fade border-green-30 flex items-center justify-between border-b py-2 pr-2 pl-3 last:border-0"
        >
          <p class="flex items-center gap-3">
            {{ member.fullName }}
            <span
              v-if="member.groupRole === GroupRole.Admin"
              class="text-coral-60 rounded-sm bg-yellow-50 px-2 py-0.5 text-xs font-bold tracking-widest uppercase"
              v-text="t('groups.admin')"
            />
          </p>
          <Button
            v-if="!isFeide && member.userId !== user?.userId"
            :aria-label="t('groups.removeMember')"
            shape="square"
            size="sm"
            @click="removeGroupMember(member.userId)"
          >
            <font-awesome-icon :icon="faXmark" />
          </Button>
        </li>
      </ul>
    </div>
    <div class="sticky -bottom-6 bg-green-50">
      <div class="grid h-full w-full grid-cols-2 gap-4 bg-green-50 py-6">
        <AuSpinner
          v-if="isLoading"
          size="sm"
        />
        <Button
          center
          shade="light"
          size="lg"
          variant="outline"
          @click="newGroup ? emit('back') : emit('changeView')"
        >
          {{ t('groups.cancel') }}
        </Button>
        <Button
          id="saveBtn"
          :disabled="!readyToSave || isLoading"
          center
          size="lg"
          @click="populateGroup"
        >
          {{ t('groups.saveGroup') }}
        </Button>
      </div>
    </div>
  </div>
</template>
